import { FC, SVGProps, useState } from 'react'
import { Box, Theme, useTheme } from '@mui/material'
import ZButton from '../components/ZButton'
import {
  CategorisationTreeOption,
  CategorisationTreeQuestion,
  SubmissionAnswer,
} from '../types'
import TreeQuestion from '../components/TreeQuestion'
import ZCheckbox from '../components/ZCheckbox'
import ZIcon from '../components/ZIcon'
import ZText from '../components/ZText'
import ZBottomContainer from '../components/ZBottomContainer'
import ZScreenContainer from '../components/ZScreenContainer'
import Header from '../components/Header'
import {
  AnswerMap,
  collectAnswers,
  hasNoneIntent,
  isComplete,
  pruneAnswers,
} from '../util/categorisationTree'
import {
  RestrictedInvestorIcon,
  HighNetWorthInvestorIcon,
  CertifiedSophisticatedInvestorIcon,
} from '../assets/icons'
import { CARD_RADIUS, SPACE } from '../styles/layout'

const categoryIcons: Record<string, FC<SVGProps<SVGSVGElement>>> = {
  RESTRICTED_INVESTOR: RestrictedInvestorIcon,
  HIGH_NET_WORTH_INVESTOR: HighNetWorthInvestorIcon,
  CERTIFIED_SOPHISTICATED_INVESTOR: CertifiedSophisticatedInvestorIcon,
}

const DECLARATION_TOPICS = [
  'HNW_DECLARATION',
  'CSI_ACCEPTANCE_DECLARATION',
  'RESTRICTED_DECLARATION',
]

const QUESTION_TAGS: Record<string, string> = {
  RESTRICTED_PAST_12_MONTHS: 'A',
  RESTRICTED_NEXT_12_MONTHS: 'B',
}

const isDeclaration = (question: CategorisationTreeQuestion) =>
  DECLARATION_TOPICS.includes(question.topic)

const CategorisationForm = ({
  rootQuestion,
  selectedOption,
  onComplete,
  recategorise,
  onLeave,
  loading,
}: {
  rootQuestion: CategorisationTreeQuestion
  selectedOption: CategorisationTreeOption
  onComplete: (answers: SubmissionAnswer[], signedAt: string) => void
  recategorise: () => void
  onLeave: () => void
  loading: boolean
}) => {
  const theme = useTheme<Theme>()

  const [answers, setAnswers] = useState<AnswerMap>({
    [rootQuestion.id]: { answerId: selectedOption.id },
  })

  // signedAt = the moment the customer ticked the declaration ("I accept …").
  const [declarationSignedAt, setDeclarationSignedAt] = useState<string>()

  const [confirmingExit, setConfirmingExit] = useState(false)

  const subtree = selectedOption.nextQuestions ?? []
  const boxQuestions = subtree.filter(q => !isDeclaration(q))
  const declarationQuestion = subtree.find(isDeclaration)
  const declarationYesOption = declarationQuestion?.options?.find(
    option => option.value === 'YES',
  )

  const declarationAccepted =
    !!declarationQuestion &&
    answers[declarationQuestion.id]?.answerId === declarationYesOption?.id

  const handleChange = (
    questionId: string,
    answer: { answerId?: string; answerInput?: string },
  ) => {
    setAnswers(previous =>
      pruneAnswers([rootQuestion], { ...previous, [questionId]: answer }),
    )
  }

  // Completeness only — every visible question (incl. declaration) answered.
  // No correctness checks: "unacceptable" inputs must be submittable (Phase 1).
  const formComplete = isComplete([rootQuestion], answers)

  const noneIntent = hasNoneIntent([rootQuestion], answers)

  const CategoryIcon = categoryIcons[selectedOption.value]

  if (confirmingExit) {
    return (
      <ZScreenContainer>
        <Header
          title={selectedOption.label}
          onBack={() => setConfirmingExit(false)}
        />

        {CategoryIcon ? (
          <ZIcon
            Icon={CategoryIcon}
            title="categorisation icon"
            style={{ alignSelf: 'center' }}
          />
        ) : null}

        <ZText>
          {
            'What would you like to do?\nPlease select one of the options below to continue.'
          }
        </ZText>

        <ZBottomContainer>
          <ZButton disabled={loading} onClick={() => recategorise()}>
            Choose a different category
          </ZButton>

          <ZButton disabled={loading} onClick={() => onLeave()}>
            I wish to leave this investment journey
          </ZButton>
        </ZBottomContainer>
      </ZScreenContainer>
    )
  }

  return (
    <ZScreenContainer>
      <Header title={selectedOption.label} onBack={() => recategorise()} />

      {CategoryIcon ? (
        <ZIcon
          Icon={CategoryIcon}
          title="categorisation icon"
          style={{ alignSelf: 'center' }}
        />
      ) : null}

      {selectedOption.statement ? (
        <ZText>{selectedOption.statement}</ZText>
      ) : null}

      {boxQuestions.length > 0 && (
        <div
          style={{
            borderColor: theme.palette.primary.main,
            borderWidth: 1,
            borderStyle: 'solid',
            borderRadius: CARD_RADIUS,
            // 16px screen gap + 16px here = 32px (space.3xl) above the box: one
            // step more than a paragraph break, so the box reads as a new
            // section rather than the next paragraph.
            marginTop: SPACE.xl,
            padding: SPACE['2xl'],
            rowGap: SPACE['2xl'],
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {/* Instruction, then each question group, each separated by a
              hairline (ink at 0.18, the same edge as cards and inputs) so a
              question's answers don't run into the next question. */}
          {selectedOption.preQuestionStatement ? (
            <ZText
              sx={{
                paddingBottom: SPACE['2xl'],
                borderBottom: '1px solid rgba(235, 235, 255, 0.18)',
              }}
            >
              {selectedOption.preQuestionStatement}
            </ZText>
          ) : null}

          {boxQuestions.map((question, index) => {
            // A follow-up (untagged) question only appears once the lettered
            // question before it has been answered. The API sends follow-ups
            // as siblings rather than children, so the gating is done here.
            if (!QUESTION_TAGS[question.topic]) {
              const parent = boxQuestions
                .slice(0, index)
                .reverse()
                .find(q => QUESTION_TAGS[q.topic])
              if (parent && answers[parent.id]?.answerId == null) return null
            }
            return (
              <div
                key={question.id}
                style={
                  // Only a lettered question (A, B, ...) starts a new group; an
                  // untagged follow-up stays with the question above it.
                  index > 0 && QUESTION_TAGS[question.topic]
                    ? {
                        paddingTop: SPACE['2xl'],
                        borderTop: '1px solid rgba(235, 235, 255, 0.18)',
                      }
                    : undefined
                }
              >
                <TreeQuestion
                  question={question}
                  answers={answers}
                  onChange={handleChange}
                  tag={QUESTION_TAGS[question.topic]}
                />
              </div>
            )
          })}
        </div>
      )}

      {boxQuestions.length === 0 && selectedOption.preQuestionStatement ? (
        <ZText>{selectedOption.preQuestionStatement}</ZText>
      ) : null}

      <ZBottomContainer>
        {declarationQuestion && declarationYesOption ? (
          // The declaration is its own section: 32px (space.3xl) above and
          // below, matching the gap above the box. 16px screen gap + 16px here
          // above; 24px here + the 8px button-stack gap below.
          <Box sx={{ width: '100%', mt: SPACE.xl, mb: SPACE['2xl'] }}>
            <ZCheckbox
              label={declarationQuestion.label}
              onChange={event => {
                if (event.target.checked) {
                  setDeclarationSignedAt(new Date().toISOString())
                }
                handleChange(declarationQuestion.id, {
                  answerId: event.target.checked
                    ? declarationYesOption.id
                    : undefined,
                })
              }}
              checked={declarationAccepted}
              // Phase 1 req: declaration checkbox is ALWAYS enabled — never
              // disable it based on the other answers' validity or presence.
              disabled={false}
            />
          </Box>
        ) : null}

        <ZButton
          loading={loading}
          onClick={() =>
            onComplete(
              collectAnswers([rootQuestion], answers),
              // The button is only enabled once the declaration is ticked, so
              // declarationSignedAt is set; fall back to now defensively.
              declarationSignedAt ?? new Date().toISOString(),
            )
          }
          disabled={!formComplete}
        >
          Next
        </ZButton>

        <ZButton
          disabled={loading}
          onClick={() =>
            noneIntent ? setConfirmingExit(true) : recategorise()
          }
        >
          {noneIntent
            ? 'Choose a different category / leave this journey'
            : 'Choose a different category'}
        </ZButton>
      </ZBottomContainer>
    </ZScreenContainer>
  )
}

export default CategorisationForm
